import * as React from 'react';
import { create } from 'zustand';

export type ToastKind = 'default' | 'success' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMsg {
  id: string;
  title: string;
  description?: string;
  kind: ToastKind;
  action?: ToastAction;
}

export interface ToastOptions {
  description?: string;
  kind?: ToastKind;
  /** Auto-dismiss after this many ms. Default 4000. Pass 0 for "until clicked". */
  durationMs?: number;
  action?: ToastAction;
}

interface State {
  toasts: ToastMsg[];
  push: (t: Omit<ToastMsg, 'id'>, durationMs: number) => string;
  remove: (id: string) => void;
}

export const useToastStore = create<State>((set) => ({
  toasts: [],
  push: (t, durationMs) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));
    if (durationMs > 0) {
      setTimeout(() => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })), durationMs);
    }
    return id;
  },
  remove: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

/** Backwards-compatible signature: `toast(title, description?, kind?)`.
 *  For richer toasts (sticky, with an action button) pass an options object as the second arg. */
export function toast(title: string, description?: string, kind?: ToastKind): string;
export function toast(title: string, options: ToastOptions): string;
export function toast(title: string, second?: string | ToastOptions, third?: ToastKind): string {
  const opts: ToastOptions =
    typeof second === 'object' && second !== null
      ? second
      : { description: second as string | undefined, kind: third };
  const durationMs = opts.durationMs ?? 4000;
  return useToastStore.getState().push(
    {
      title,
      description: opts.description,
      kind: opts.kind ?? 'default',
      action: opts.action,
    },
    durationMs,
  );
}

export function dismissToast(id: string) {
  useToastStore.getState().remove(id);
}

export function Toaster() {
  const { toasts, remove } = useToastStore();
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`rounded-md border p-4 shadow-md min-w-[280px] max-w-md ${
            t.kind === 'error' ? 'bg-destructive text-destructive-foreground' :
            t.kind === 'success' ? 'bg-green-600 text-white' : 'bg-background'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 cursor-pointer" onClick={() => remove(t.id)}>
              <div className="font-medium">{t.title}</div>
              {t.description && <div className="text-sm opacity-90 whitespace-pre-line">{t.description}</div>}
            </div>
            {t.action && (
              <button
                type="button"
                onClick={() => { t.action!.onClick(); remove(t.id); }}
                className="text-xs font-semibold underline underline-offset-2 hover:opacity-80 shrink-0 mt-0.5"
              >
                {t.action.label}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
